import { KuxError, ServicesListService, tvpilGetLocalCache, UserServiceTariff } from '@kaltura-ott/tvpil';
import { action, observable } from 'mobx';
import { container, singleton } from 'tsyringe';

import { errorHandling } from '../../utils/error-handling';
import { AppParams } from '../app/app-params';
import type { AppParametersStore } from '../app/app-params-store';
import { ErrorPriority } from '../error';

import type { LoginStore } from './login-store';

@singleton()
export class ServicesStore {
  static moduleName = 'ServicesStore';
  static readonly WRONG_PASSWORD_ERROR_CODE = '1011';

  constructor() {}
  params?: AppParams;

  @observable private ks = '';
  @observable service?: UserServiceTariff;
  @observable servicesList?: UserServiceTariff[] = [];
  @observable serviceListRequiredToPresent?: boolean;

  private servicesListService?: ServicesListService;

  private get loginStore() {
    return container.resolve<LoginStore>('loginStore');
  }

  @errorHandling({ type: ErrorPriority.high })
  init(ks: string) {
    this.ks = ks;
    const appParametersStore = container.resolve<AppParametersStore>('appParamsStore');
    this.params = appParametersStore.params;
    this.servicesListService = new ServicesListService(ks);
  }

  initializeServicesListService(ks: string) {
    this.servicesListService = new ServicesListService(ks);
  }

  /**
   * Get services list by `token` from SSO authorization by username and password
   *  @param token: string
   *  @returns Promise<UserServiceTariff[]>
   */
  @action.bound
  async fetchServicesListPage(token: string): Promise<UserServiceTariff[]> {
    this.serviceListRequiredToPresent = true;
    if (!this.servicesListService) {
      const cache = tvpilGetLocalCache();
      this.ks = (await cache.getItem('ks')) as string;
      this.initializeServicesListService(this.ks);
    }
    this.servicesListService?.reset();
    this.servicesList = [];

    this.servicesListService?.setBearer(token);
    this.servicesListService?.setParams(this.params);

    if (this.servicesListService && this.servicesListService.hasMore()) {
      const newServices = await this.servicesListService.fetchNext();
      if (newServices.length) {
        this.servicesList = this.servicesList.concat(newServices);
      }
      return this.servicesList;
    }

    return [];
  }

  /**
   * Get services list by `id`. Your current service will be able in `service` property.
   *  @param id: string
   *  @returns Promise UserServiceTariff
   */
  @action.bound
  @errorHandling({
    type: ErrorPriority.high,
    skipRetry: true,
    callback: function (this: ServicesStore, error) {
      this.loginStore.errorHandlerCallback(error);
    },
  })
  async selectService(id: string, skipRefresh = false): Promise<UserServiceTariff | undefined> {
    try {
      await this.loginStore.loginByJwtToken(id);
      this.service = this.servicesList?.find((service) => service.id === id);
      this.serviceListRequiredToPresent = false;
      return this.service;
    } catch (e) {
      if (!skipRefresh && (e as KuxError).code === ServicesStore.WRONG_PASSWORD_ERROR_CODE) {
        await this.loginStore.refreshJwtToken();
        return this.selectService(id, true);
      }

      throw e;
    }
  }

  /**
   * Reset services store to the default state.
   *  @returns void
   */
  @action.bound
  resetStore() {
    this.ks = '';
    this.servicesList = [];
    this.service = undefined;
    this.servicesListService?.reset();
  }
}
